/**
 * Exact rational arithmetic backed by bigint.
 *
 * Every value is stored in fully reduced form with a strictly positive
 * denominator, so equality is structural and division by zero is impossible
 * to represent. No floating point is used anywhere in this module, which is
 * what lets the rest of the application treat generated answers as exact.
 */

function gcd(a: bigint, b: bigint): bigint {
  let x = a < 0n ? -a : a;
  let y = b < 0n ? -b : b;
  while (y !== 0n) {
    [x, y] = [y, x % y];
  }
  return x;
}

export class Rational {
  /** Numerator, carrying the sign of the value. */
  readonly n: bigint;
  /** Denominator, always strictly positive. */
  readonly d: bigint;

  private constructor(n: bigint, d: bigint) {
    this.n = n;
    this.d = d;
  }

  /**
   * Build a Rational from a numerator and (optional) denominator.
   * Throws on a zero denominator - callers must never be able to create an
   * undefined value.
   */
  static of(n: bigint | number, d: bigint | number = 1n): Rational {
    const nn = typeof n === "number" ? Rational.fromSafeInteger(n) : n;
    const dd = typeof d === "number" ? Rational.fromSafeInteger(d) : d;
    if (dd === 0n) {
      throw new RangeError("Rational denominator cannot be zero");
    }
    // Normalize sign so the denominator is always positive.
    let num = nn;
    let den = dd;
    if (den < 0n) {
      num = -num;
      den = -den;
    }
    const g = gcd(num, den);
    if (g > 1n) {
      num /= g;
      den /= g;
    }
    return new Rational(num, den);
  }

  private static fromSafeInteger(x: number): bigint {
    if (!Number.isInteger(x)) {
      throw new TypeError(`Rational requires integer numbers, received ${x}`);
    }
    return BigInt(x);
  }

  static readonly ZERO = Rational.of(0n);
  static readonly ONE = Rational.of(1n);

  add(o: Rational): Rational {
    return Rational.of(this.n * o.d + o.n * this.d, this.d * o.d);
  }

  sub(o: Rational): Rational {
    return Rational.of(this.n * o.d - o.n * this.d, this.d * o.d);
  }

  mul(o: Rational): Rational {
    return Rational.of(this.n * o.n, this.d * o.d);
  }

  /** Throws if dividing by zero. */
  div(o: Rational): Rational {
    if (o.isZero()) {
      throw new RangeError("Rational division by zero");
    }
    return Rational.of(this.n * o.d, this.d * o.n);
  }

  neg(): Rational {
    return Rational.of(-this.n, this.d);
  }

  abs(): Rational {
    return this.n < 0n ? this.neg() : this;
  }

  /** Multiplicative inverse. Throws if this is zero. */
  reciprocal(): Rational {
    if (this.isZero()) {
      throw new RangeError("Cannot invert zero");
    }
    return Rational.of(this.d, this.n);
  }

  isZero(): boolean {
    return this.n === 0n;
  }

  isInteger(): boolean {
    return this.d === 1n;
  }

  isNegative(): boolean {
    return this.n < 0n;
  }

  isPositive(): boolean {
    return this.n > 0n;
  }

  /** -1, 0, or 1 for this compared to o. */
  compare(o: Rational): -1 | 0 | 1 {
    const lhs = this.n * o.d;
    const rhs = o.n * this.d;
    if (lhs < rhs) return -1;
    if (lhs > rhs) return 1;
    return 0;
  }

  equals(o: Rational): boolean {
    return this.n === o.n && this.d === o.d;
  }

  lt(o: Rational): boolean {
    return this.compare(o) < 0;
  }
  lte(o: Rational): boolean {
    return this.compare(o) <= 0;
  }
  gt(o: Rational): boolean {
    return this.compare(o) > 0;
  }
  gte(o: Rational): boolean {
    return this.compare(o) >= 0;
  }

  /** Approximate decimal value. For display heuristics only, never for math. */
  toNumber(): number {
    return Number(this.n) / Number(this.d);
  }

  /** Plain-text form: "3", "-3", "3/5", "-3/5". */
  toString(): string {
    if (this.isInteger()) {
      return this.n.toString();
    }
    return `${this.n.toString()}/${this.d.toString()}`;
  }

  /**
   * KaTeX/LaTeX form. Integers render bare; fractions render with \frac,
   * pushing a negative sign outside the fraction for cleaner typography.
   */
  toLatex(): string {
    if (this.isInteger()) {
      return this.n.toString();
    }
    if (this.n < 0n) {
      return `-\\frac{${(-this.n).toString()}}{${this.d.toString()}}`;
    }
    return `\\frac{${this.n.toString()}}{${this.d.toString()}}`;
  }

  /** Stable structural key, useful for de-duplication and history signatures. */
  key(): string {
    return `${this.n.toString()}/${this.d.toString()}`;
  }
}

/** Convenience constructor. */
export function rat(n: bigint | number, d: bigint | number = 1n): Rational {
  return Rational.of(n, d);
}
